import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CmmSubTitle, moneyInKorean, SalesTable, SalesTableBody, SalesTableCell, SalesTableRow } from 'components/Dashboard';
import 'moment/locale/ko';
import moment from 'moment';

const ChipRenderer = (props) => {
  const { value } = props;

  const chipVariant = value ? 'filled' : 'outlined';
  const chipColor = value ? 'success' : 'error';

  return <Chip size="small" label={value ? '완료' : '미완료'} color={chipColor} variant={chipVariant} sx={{ width: '100%' }} />;
};

const columns = [
  { field: 'id', headerName: 'ID', width: 90, hide: true },
  { field: 'rowNumber', headerName: 'No', width: 90 },
  {
    field: 'orderNo',
    headerName: '주문번호',
    headerAlign: 'center',
    width: 120
  },
  {
    field: 'isIncome',
    headerName: '수입계산서 여부',
    headerAlign: 'center',
    align: 'center',
    width: 130,
    renderCell: (params) => <ChipRenderer value={params.value} />
  },
  {
    field: 'depositAt',
    headerName: '입금일',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    width: 160
  },
  {
    field: 'amount',
    headerName: '총 금액',
    headerAlign: 'center',
    description: '총 금액 = 판매 금액 - 할인 금액',
    type: 'number',
    width: 150,
    valueGetter: (params) => Number(params.value)
  },
  {
    field: 'vat',
    headerName: 'VAT',
    headerAlign: 'center',
    type: 'number',
    width: 120,
    valueGetter: (params) => Number(params.row.amount) * 0.1
  },
  {
    field: 'depositAmount',
    headerName: '최종 금액',
    headerAlign: 'center',
    description: '최종결제금액 = 총 금액 + VAT',
    type: 'number',
    width: 150,
    valueGetter: (params) => Number(params.value)
  },
  {
    field: 'createdName',
    headerName: '구매자',
    headerAlign: 'center',
    sortable: false,
    width: 120
  },
  {
    field: 'acCompanyName',
    headerName: '구매업체',
    headerAlign: 'center',
    sortable: false,
    width: 150
  }
];

const incomeColumns = [
  { field: 'id', headerName: 'ID', width: 90, hide: true },
  { field: 'incomeAt', headerName: '작성일자', headerAlign: 'center', align: 'center', width: 120 },
  { field: 'amountKor', headerName: '금액원정', headerAlign: 'center', align: 'right', width: 200 },
  { field: 'amount', headerName: '금액', headerAlign: 'center', align: 'right', width: 120, valueGetter: (params) => params.value.toLocaleString() },
  { field: 'prjNo', headerName: '사업번호', headerAlign: 'center', align: 'center', width: 80 },
  { field: 'prjNm', headerName: '사업명', headerAlign: 'center', align: 'center', width: 80 },
  { field: 'dept', headerName: '주무배치', headerAlign: 'center', align: 'center', width: 120 },
  { field: 'subject', headerName: '과목', headerAlign: 'center', align: 'center', width: 120 },
  { field: 'managerDept', headerName: '담당부서', headerAlign: 'center', align: 'center', width: 120 },
  { field: 'managerNm', headerName: '담당자', headerAlign: 'center', align: 'center', width: 120 }
];

const cellStyles = { thCell: { width: '20%' }, inputCell: { p: 0, width: '30%' } };

export default function OrderIncome(props) {
  const { open, onClose, openType, srchParams } = props;
  return (
    <>
      {openType === 'regist' && <IncomeRegist open={open} onClose={onClose} srchParams={srchParams} />}
      {openType === 'detail' && <IncomeDetail open={open} onClose={onClose} />}
    </>
  );
}

const IncomeRegist = (props) => {
  const { open, onClose, srchParams } = props;

  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const [rows, setRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [srchDatas, setSrchDatas] = useState({ ...srchParams });
  const [datas, setDatas] = useState({
    year: year.toString(),
    month: month.toString(),
    day: day.toString(),
    prjNo: 'A004',
    prjNm: '공통',
    dept: '기술총괄부',
    subject: '도화 앱스사용권',
    managerDept: '기술총괄부',
    managerNm: '담당자명'
  });

  const handleClose = () => {
    onClose();
  };

  const handleChange = (e) => {
    setDatas({ ...datas, [e.target.id]: e.target.value });
  };

  const handleChangeNumber = (e, limit) => {
    const value = e.target.value;
    if (value === '' || (!isNaN(value) && parseInt(value) <= limit)) {
      setDatas({ ...datas, [e.target.id]: value });
    }
  };

  const getSelectedAmount = (items, orderIds) => {
    if (items.length === 0 && orderIds.length === 0) return Number(0);
    let amount = items
      .filter((t) => orderIds.includes(t.id))
      .map((t) => t.depositAmount)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return !isNaN(amount) ? amount : Number(0);
  };

  const getSelectedAmountKor = (amount) => {
    const numberAmount = Number(amount);
    return !isNaN(numberAmount) ? moneyInKorean(numberAmount) : '';
  };

  const handleClickSave = async (e) => {
    const { year, month, day } = datas;
    let insertParams = { ...datas, isIncome: rowSelectionModel };
    let orderIds = []; //수입계산서 작성 완료 처리할 주문 ID 목록
    let orderNos = []; //수입계산서 작성 완료 처리할 주문번호 목록

    if (rowSelectionModel.length === 0) {
      alert('작성 완료할 주문을 선택하세요');
      return;
    }

    //수입계산서 완료할 주문 총액 계산
    const amount = getSelectedAmount(rows, rowSelectionModel);
    const amountKor = getSelectedAmountKor(amount);
    insertParams.amount = amount;
    insertParams.amountKor = amountKor;

    //수입계산서 작성일자
    const date = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    const isValidDate = moment(date, 'YYYY-MM-DD', true).isValid();
    const formattedDate = isValidDate ? moment(date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    insertParams.incomeAt = formattedDate;

    //수입계산서 처리 완료 목록
    rows.forEach((row) => {
      const selected = rowSelectionModel.includes(row.id);
      if (selected) {
        orderIds.push(row.id);
        orderNos.push(row.orderNo);
      }
    });
    insertParams.orderIds = orderIds;
    insertParams.orderNos = orderNos;

    try {
      if (window.confirm('수입계산서 등록 후 내용을 변경할 수 없습니다. 수입계산서를 작성하시겠습니까?')) {
        await axios
          .post(`/api/dashboard/order-hist`, insertParams)
          .then((response) => {
            alert(response.data.message);
            onClose(response.data);
          })
          .catch((error) => {
            alert(error.data.message);
          });
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getDataList = async (e) => {
    const { startYmd, endYmd } = srchDatas;

    if (!!!startYmd) {
      alert('발급 시작일을 입력해 주세요.');
      return;
    }
    if (!!!endYmd) {
      alert('발급 종료일을 입력해 주세요.');
      return;
    }

    let params = {
      startYmd: moment(startYmd).format('YYYY-MM-DD'),
      endYmd: moment(endYmd).format('YYYY-MM-DD'),
      isIncome: false
    };

    const response = await axios.get(`/api/dashboard/order-hist`, { params });
    if (response.data) {
      const result = response.data?.rows;
      setRows(result);
      setRowSelectionModel(result.map((t) => t.id));
    }
  };

  useEffect(() => {
    getDataList();
  }, []);

  const amount = getSelectedAmount(rows, rowSelectionModel);
  const amountKor = getSelectedAmountKor(amount);

  return (
    <Dialog onClose={handleClose} open={open} fullWidth={true} maxWidth="lg">
      <DialogTitle>수입계산서 등록</DialogTitle>
      <DialogContent>
        <CmmSubTitle title="수입계산서 미작성 목록" />
        <DataGrid
          sx={{ height: 300 }}
          rowHeight={30}
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              }
            }
          }}
          pageSizeOptions={[10]}
          checkboxSelection
          // disableRowSelectionOnClick
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
        />
        <CmmSubTitle title="수입계산서 입력 항목" sx={{ mt: 2 }} />
        <SalesTable>
          <SalesTableBody>
            <SalesTableRow>
              <SalesTableCell th sx={cellStyles.thCell}>
                작성일자 및 담당
              </SalesTableCell>
              <SalesTableCell sx={{ ...cellStyles.inputCell, width: '30%' }}>
                <TextField
                  id="year"
                  value={datas?.year}
                  size="small"
                  sx={{ width: 100 }}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">年</InputAdornment>
                  }}
                />
                <TextField
                  id="month"
                  value={datas?.month}
                  size="small"
                  sx={{ width: 80 }}
                  // onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">月</InputAdornment>,
                    maxLength: 2,
                    onInput: (e) => handleChangeNumber(e, 12)
                  }}
                />
                <TextField
                  id="day"
                  value={datas?.day}
                  size="small"
                  sx={{ width: 80 }}
                  // onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">日</InputAdornment>,
                    maxLength: 2,
                    onInput: (e) => handleChangeNumber(e, 31)
                  }}
                />
              </SalesTableCell>
              <SalesTableCell sx={{ ...cellStyles.inputCell, textAlign: 'right', width: '60%' }}>
                <TextField
                  id="managerDept"
                  value={datas?.managerDept}
                  size="small"
                  sx={{ width: 200 }}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">陪</InputAdornment>
                  }}
                />
                <TextField
                  id="managerNm"
                  value={datas?.managerNm}
                  size="small"
                  sx={{ width: 200 }}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">(인)</InputAdornment>
                  }}
                />
              </SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th unit="원 정" sx={{ width: '50%' }}>
                <span style={{ fontWeight: 'bold' }}>金</span> {amountKor}
              </SalesTableCell>
              <SalesTableCell th sx={{ width: '50%', textAlign: 'right' }}>
                ￦ {amount}
              </SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={cellStyles.thCell}>
                事業番號 (사업번호)
              </SalesTableCell>
              <SalesTableCell sx={cellStyles.inputCell}>
                <TextField id="prjNo" value={datas?.prjNo} size="small" onChange={handleChange} fullWidth />
              </SalesTableCell>
              <SalesTableCell th sx={cellStyles.thCell}>
                事業名 (사업명)
              </SalesTableCell>
              <SalesTableCell sx={cellStyles.inputCell}>
                <TextField id="prjNm" value={datas?.prjNm} size="small" onChange={handleChange} fullWidth />
              </SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={cellStyles.thCell}>
                主務배치 (주무배치)
              </SalesTableCell>
              <SalesTableCell sx={cellStyles.inputCell}>
                <TextField id="dept" value={datas?.dept} size="small" onChange={handleChange} fullWidth />
              </SalesTableCell>
              <SalesTableCell th sx={cellStyles.thCell}>
                科目 (과목)
              </SalesTableCell>
              <SalesTableCell sx={cellStyles.inputCell}>
                <TextField id="subject" value={datas?.subject} size="small" onChange={handleChange} fullWidth />
              </SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={{ width: '50%' }}>
                {datas?.year} 年 {datas?.month} 月 {datas?.day} 日
              </SalesTableCell>
              <SalesTableCell th sx={{ textAlign: 'right', width: '50%' }}>
                {datas?.managerDept} 陪 {datas?.managerNm} (인)
              </SalesTableCell>
            </SalesTableRow>
          </SalesTableBody>
        </SalesTable>
        <span style={{ fontSize: '11px' }}>
          * 결재라인 : 회장 - <span style={{ textDecoration: 'line-through' }}>부회장 - 부서장 - 배치장 - 임원</span> - 담당 (담당자 싸인 후 원장님 결재)
        </span>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClickSave}>
          수입계산서 등록
        </Button>
        <Button variant="contained" onClick={handleClose}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const IncomeDetail = (props) => {
  const { open, onClose } = props;

  const [rows, setRows] = useState([]);
  const [datas, setDatas] = useState({});

  const getDataList = async (e) => {
    const response = await axios.get(`/api/dashboard/order-income`, { params: {} });
    if (response.data) {
      setRows(response.data);
    }
  };

  const handleRowClick = (params) => {
    setDatas(params.row);
  };

  useEffect(() => {
    getDataList();
  }, []);

  let incomeYmd = {};
  if (datas?.incomeAt) {
    const incomeAt = datas?.incomeAt.split('-');
    incomeYmd.year = incomeAt[0] || '';
    incomeYmd.month = incomeAt[1] || '';
    incomeYmd.day = incomeAt[2] || '';
  }

  return (
    <Dialog onClose={onClose} open={open} fullWidth={true} maxWidth="lg">
      <DialogTitle>수입계산서 상세보기</DialogTitle>
      <DialogContent>
        <CmmSubTitle title="수입계산서 목록" />
        <DataGrid
          sx={{ height: 300 }}
          rowHeight={30}
          rows={rows}
          columns={incomeColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              }
            }
          }}
          pageSizeOptions={[10]}
          onRowClick={handleRowClick}
        />
        <CmmSubTitle title="수입계산서 상세" sx={{ mt: 2 }} />
        <SalesTable>
          <SalesTableBody>
            <SalesTableRow>
              <SalesTableCell th unit="원 정" sx={{ width: '50%' }}>
                <span style={{ fontWeight: 'bold' }}>金</span> {datas?.amountKor}
              </SalesTableCell>
              <SalesTableCell th sx={{ width: '50%', textAlign: 'right' }}>
                ￦ {datas?.amount?.toLocaleString()}
              </SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={cellStyles.thCell}>
                事業番號 (사업번호)
              </SalesTableCell>
              <SalesTableCell sx={{ width: '30%' }}>{datas?.prjNo}</SalesTableCell>
              <SalesTableCell th sx={cellStyles.thCell}>
                事業名 (사업명)
              </SalesTableCell>
              <SalesTableCell>{datas?.prjNm}</SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={cellStyles.thCell}>
                主務배치 (주무배치)
              </SalesTableCell>
              <SalesTableCell sx={{ width: '30%' }}>{datas?.dept}</SalesTableCell>
              <SalesTableCell th sx={cellStyles.thCell}>
                科目 (과목)
              </SalesTableCell>
              <SalesTableCell>{datas?.subject}</SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={{ width: '50%' }}>
                {incomeYmd?.year} 年 {incomeYmd?.month} 月 {incomeYmd?.day} 日
              </SalesTableCell>
              <SalesTableCell th sx={{ textAlign: 'right', width: '50%' }}>
                {datas?.managerDept} 陪 {datas?.managerNm} (인)
              </SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={cellStyles.thCell}>
                주문번호 ({datas?.orderNos?.length})건
              </SalesTableCell>
              <SalesTableCell>{datas?.orderNos?.join(' | ')}</SalesTableCell>
            </SalesTableRow>
          </SalesTableBody>
        </SalesTable>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};
